import * as React from "react";
import { format, parse, isValid, addMonths, subMonths } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight } from "lucide-react";
import { UseFormReturn } from "react-hook-form";
import { DatePicker } from "antd";
import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/pt-br";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { FormField, FormItem, FormLabel, FormMessage, FormControl } from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PipelineFieldRow, PIPELINE_INPUT_CLASS, PIPELINE_DATE_SUFFIX } from "@/components/form/pipelineFieldRow";
import { usePipelineForm } from "@/components/form/PipelineFormContext";

dayjs.locale("pt-br");

type DateFieldVariant = "default" | "pipeline";

interface DateFieldProps {
  form: UseFormReturn<any>;
  name: string;
  label: string;
  className?: string;
  disabled?: boolean;
  placeholder?: string;
  allowManualEdit?: boolean;
  showMonthYearPicker?: boolean;
  variant?: DateFieldVariant;
  disableFuture?: boolean;
}

function toDayjs(value: unknown): Dayjs | null {
  if (!value) return null;
  if (value instanceof Date && isValid(value)) return dayjs(value);
  if (typeof value === "string") {
    const parsed = parse(value, "yyyy-MM-dd", new Date());
    if (isValid(parsed)) return dayjs(parsed);
    const asDate = new Date(value);
    if (isValid(asDate)) return dayjs(asDate);
  }
  return null;
}

function toFieldValue(date: Dayjs | null): string | undefined {
  return date ? date.format("YYYY-MM-DD") : undefined;
}

export function DateField({
  form,
  name,
  label,
  className,
  disabled,
  placeholder = "dd/mm/aaaa",
  allowManualEdit = true,
  showMonthYearPicker = true,
  variant,
  disableFuture = false,
}: DateFieldProps) {
  const { variant: contextVariant } = usePipelineForm();
  const resolvedVariant = variant ?? contextVariant;
  const isPipeline = resolvedVariant === "pipeline";

  const [isOpen, setIsOpen] = React.useState(false);
  const [inputValue, setInputValue] = React.useState("");
  const [currentMonth, setCurrentMonth] = React.useState<Date>(new Date());

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 110 }, (_, i) => currentYear - 50 + i);

  const months = [
    "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
    "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro",
  ];

  return (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => {
        React.useEffect(() => {
          if (field.value) {
            const dateValue =
              typeof field.value === "string"
                ? parse(field.value, "yyyy-MM-dd", new Date())
                : field.value;

            if (isValid(dateValue)) {
              setInputValue(format(dateValue, "dd/MM/yyyy", { locale: ptBR }));
              setCurrentMonth(dateValue);
            } else {
              setInputValue("");
            }
          } else {
            setInputValue("");
          }
        }, [field.value]);

        const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
          const value = e.target.value;
          setInputValue(value);

          const cleanValue = value.replace(/[^\d/]/g, "");
          let formattedValue = cleanValue;
          if (cleanValue.length >= 2 && !cleanValue.includes("/")) {
            formattedValue = cleanValue.slice(0, 2) + "/" + cleanValue.slice(2);
          }
          if (cleanValue.length >= 5 && cleanValue.split("/").length === 2) {
            formattedValue = cleanValue.slice(0, 5) + "/" + cleanValue.slice(5, 9);
          }

          setInputValue(formattedValue);

          if (formattedValue.length === 10) {
            try {
              const parsedDate = parse(formattedValue, "dd/MM/yyyy", new Date());
              if (isValid(parsedDate)) {
                field.onChange(format(parsedDate, "yyyy-MM-dd"));
              }
            } catch {
              // data inválida
            }
          }
        };

        const handleInputBlur = () => {
          if (inputValue && inputValue.length === 10) {
            try {
              const parsedDate = parse(inputValue, "dd/MM/yyyy", new Date());
              if (isValid(parsedDate)) {
                field.onChange(format(parsedDate, "yyyy-MM-dd"));
                setInputValue(format(parsedDate, "dd/MM/yyyy", { locale: ptBR }));
              } else if (field.value) {
                const originalDate =
                  typeof field.value === "string"
                    ? parse(field.value, "yyyy-MM-dd", new Date())
                    : field.value;
                setInputValue(format(originalDate, "dd/MM/yyyy", { locale: ptBR }));
              } else {
                setInputValue("");
              }
            } catch {
              if (field.value) {
                const originalDate =
                  typeof field.value === "string"
                    ? parse(field.value, "yyyy-MM-dd", new Date())
                    : field.value;
                setInputValue(format(originalDate, "dd/MM/yyyy", { locale: ptBR }));
              } else {
                setInputValue("");
              }
            }
          }
        };

        const handleCalendarSelect = (date: Date | undefined) => {
          const dateString = date ? format(date, "yyyy-MM-dd") : undefined;
          field.onChange(dateString);

          if (date && date instanceof Date) {
            setInputValue(format(date, "dd/MM/yyyy", { locale: ptBR }));
            setCurrentMonth(date);
          }
          setIsOpen(false);
        };

        const handleMonthChange = (monthIndex: number) => {
          const safeCurrentMonth = currentMonth instanceof Date ? currentMonth : new Date();
          setCurrentMonth(new Date(safeCurrentMonth.getFullYear(), monthIndex, 1));
        };

        const handleYearChange = (year: string) => {
          const safeCurrentMonth = currentMonth instanceof Date ? currentMonth : new Date();
          setCurrentMonth(new Date(parseInt(year, 10), safeCurrentMonth.getMonth(), 1));
        };

        const navigateMonth = (direction: "prev" | "next") => {
          const safeCurrentMonth = currentMonth instanceof Date ? currentMonth : new Date();
          setCurrentMonth(
            direction === "prev"
              ? subMonths(safeCurrentMonth, 1)
              : addMonths(safeCurrentMonth, 1)
          );
        };

        const errorMessage = form.formState.errors[name]?.message as string | undefined;

        if (isPipeline) {
          return (
            <FormItem className={cn("flex flex-col p-0 space-y-0", className)}>
              <PipelineFieldRow label={label} error={errorMessage}>
                <DatePicker
                  value={toDayjs(field.value)}
                  onChange={(date) => field.onChange(toFieldValue(date))}
                  format="DD/MM/YYYY"
                  placeholder={placeholder === "dd/mm/aaaa" ? "Selecione" : placeholder}
                  variant="borderless"
                  className={PIPELINE_INPUT_CLASS}
                  style={{ width: "100%" }}
                  disabled={disabled}
                  suffixIcon={PIPELINE_DATE_SUFFIX}
                  disabledDate={
                    disableFuture
                      ? (current) => current && current.endOf("day").isAfter(dayjs().endOf("day"))
                      : undefined
                  }
                  status={errorMessage ? "error" : undefined}
                />
              </PipelineFieldRow>
            </FormItem>
          );
        }

        return (
          <FormItem className={cn("flex flex-col", className)}>
            <FormLabel>{label}</FormLabel>
            <div className="flex gap-2">
              {allowManualEdit && (
                <FormControl>
                  <Input
                    placeholder={placeholder}
                    value={inputValue}
                    onChange={handleInputChange}
                    onBlur={handleInputBlur}
                    onFocus={() => setIsOpen(false)}
                    disabled={disabled}
                    className="flex-1"
                  />
                </FormControl>
              )}
              <Popover open={isOpen} onOpenChange={setIsOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="icon" disabled={disabled} className="shrink-0">
                    <CalendarIcon className="h-4 w-4" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  {showMonthYearPicker && (
                    <div className="flex items-center justify-between p-3 border-b">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigateMonth("prev")}
                        className="h-8 w-8 p-0"
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </Button>

                      <div className="flex gap-2">
                        <Select
                          value={(currentMonth instanceof Date
                            ? currentMonth.getMonth()
                            : new Date().getMonth()
                          ).toString()}
                          onValueChange={(value) => handleMonthChange(parseInt(value, 10))}
                        >
                          <SelectTrigger className="w-24 h-8">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {months.map((month, index) => (
                              <SelectItem key={index} value={index.toString()}>
                                {month}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>

                        <Select
                          value={(currentMonth instanceof Date
                            ? currentMonth.getFullYear()
                            : new Date().getFullYear()
                          ).toString()}
                          onValueChange={handleYearChange}
                        >
                          <SelectTrigger className="w-20 h-8">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {years.map((year) => (
                              <SelectItem key={year} value={year.toString()}>
                                {year}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigateMonth("next")}
                        className="h-8 w-8 p-0"
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  )}

                  <Calendar
                    mode="single"
                    selected={
                      field.value
                        ? typeof field.value === "string"
                          ? parse(field.value, "yyyy-MM-dd", new Date())
                          : field.value
                        : undefined
                    }
                    onSelect={handleCalendarSelect}
                    disabled={disabled}
                    locale={ptBR}
                    month={currentMonth instanceof Date ? currentMonth : new Date()}
                    onMonthChange={setCurrentMonth}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
}

export default DateField;
